import React, { Suspense } from "react"
import { Analytics } from "@vercel/analytics/react"
import { useTranslation } from "react-i18next"
import { Header, Footer } from "./LandingPage"
import { Waves, Users, Rocket, Mail } from "lucide-react"
import GradientBackground from './sections/GradientBackground'
import SEO from './common/SEO'
const AccordionSection = React.lazy(() => import('./sections/AccordionSection'))

function AccordionSkeleton() {
  return (
    <div className="space-y-4 w-full animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-gray-100 h-16 rounded-lg" />
      ))}
    </div>
  )
}

const AboutPage: React.FC = () => {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/about" />
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="px-3 pt-3">
          <div className="panel-hero rounded-[2.5rem]">
          <div className="container flex flex-col items-center justify-center space-y-5 pt-40 md:pt-48 pb-20 text-center relative z-20">
            <h1 className="max-w-3xl text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
              {t('about.hero.title')}<br />
              <span className="text-[#1F80FF]">{t('about.hero.titleHighlight')}</span>
            </h1>
            <p className="max-w-[600px] text-lg md:text-xl text-muted-foreground">
              {t('about.hero.body')}
            </p>
          </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="container py-20 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1200px] mx-auto">
            <div className="panel-tint-blue rounded-[2rem] p-8">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                <Waves className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('about.mission.oceanFirst.title')}</h3>
              <p className="text-muted-foreground">
                {t('about.mission.oceanFirst.body')}
              </p>
            </div>

            <div className="panel-tint-lavender rounded-[2rem] p-8">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                <Users className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('about.mission.community.title')}</h3>
              <p className="text-muted-foreground">
                {t('about.mission.community.body')}
              </p>
            </div>

            <div className="panel-tint-mint rounded-[2rem] p-8">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 shadow-sm">
                <Rocket className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t('about.mission.tech.title')}</h3>
              <p className="text-muted-foreground">
                {t('about.mission.tech.body')}
              </p>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="container py-20 relative z-20">
          <div className="panel-dark rounded-[2.5rem] py-20 px-6">
            <div className="max-w-[800px] mx-auto text-center space-y-16">
              <div className="space-y-6">
                <p className="text-xl text-white/80 leading-relaxed">
                  {t('about.story.p1.part1')}<span className="text-[#4FA8FF]">{t('about.story.p1.highlight1')}</span>{t('about.story.p1.part2')}<span className="text-[#4FA8FF]">{t('about.story.p1.highlight2')}</span>{t('about.story.p1.part3')}
                </p>
              </div>

              <div className="space-y-6">
                <p className="text-xl text-white/80 leading-relaxed">
                  {t('about.story.p2.part1')}<span className="text-[#4FA8FF]">{t('about.story.p2.highlight1')}</span>{t('about.story.p2.part2')}<span className="text-[#4FA8FF]">{t('about.story.p2.highlight2')}</span>{t('about.story.p2.part3')}
                </p>
              </div>

              <div className="space-y-6">
                <p className="text-xl text-white/80 leading-relaxed">
                  {t('about.story.p3.part1')}<span className="text-[#4FA8FF]">{t('about.story.p3.highlight1')}</span>{t('about.story.p3.part2')}<span className="text-[#4FA8FF]">{t('about.story.p3.highlight2')}</span>{t('about.story.p3.part3')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container py-20 relative z-20">
          <div className="max-w-[800px] mx-auto space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-block rounded-full bg-[#1F80FF]/10 px-3 py-1 text-sm text-[#1F80FF]">
                {t('about.faq.badge')}
              </div>
              <h2 className="text-3xl font-bold">{t('about.faq.title')}</h2>
              <p className="text-lg text-muted-foreground">
                {t('about.faq.subtitle')}
              </p>
            </div>

            <Suspense fallback={<AccordionSkeleton />}>
              <AccordionSection />
            </Suspense>
          </div>
        </section>
      
        {/* Contact Section */}
        <section className="container py-20 relative z-20">
          <div className="max-w-[600px] mx-auto text-center space-y-8">
            <div className="inline-flex items-center space-x-2 bg-[#1F80FF]/10 rounded-full px-4 py-2">
              <Mail className="w-5 h-5 text-[#1F80FF]" />
              <span className="text-[#1F80FF] font-medium">{t('about.contact.badge')}</span>
            </div>
            <h2 className="text-3xl font-black tracking-tight">{t('about.contact.title')}</h2>
            <p className="text-lg text-muted-foreground">
              {t('about.contact.body')}
            </p>
            <a 
              href="mailto:hello@deepsealabs.co"
              className="inline-block text-xl text-[#1F80FF] hover:text-[#1F80FF]/80 font-medium"
            >
              hello@deepsealabs.co
            </a>
          </div>
        </section>
      </main>
      <GradientBackground />
      <Footer />
      <Analytics />
    </div>
  )
}

export default AboutPage 